/**
 * Terms of Service Page
 * 
 * Legal document for NewAudia Systems, LLC
 * Version 1.1 | Effective Date: March 1, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { useAuth } from '../contexts/AuthContext';
import { Seo, PAGE_SEO } from '../components/Seo';

const TermsOfServicePage = () => {
  const { user } = useAuth();
  
  const getHomeRoute = () => {
    if (!user) return '/';
    const roles = user.roles || [];
    if (roles.includes('admin')) return '/admin';
    if (roles.includes('publisher')) return '/publisher';
    if (roles.includes('author') || roles.includes('creator')) return '/creator';
    if (roles.includes('reader')) return '/home';
    return '/';
  };
  
  const homeRoute = getHomeRoute();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Seo {...PAGE_SEO.terms} />
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center gap-3">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-10 w-auto rounded-lg" />
          </Link>
          <Link to={homeRoute} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Document Header */}
        <div className="mb-12 pb-8 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-6">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-16 w-auto rounded-lg" />
            <h1 data-testid="tos-company-name" className="text-3xl font-bold text-white">NewAudia Systems, LLC</h1>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-6 h-6 text-indigo-400" />
            <h2 data-testid="tos-title" className="text-2xl font-semibold text-white">Terms of Service</h2>
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <p><span className="text-slate-500">Effective Date:</span> March 1, 2026</p>
            <p><span className="text-slate-500">Version:</span> 1.1</p>
            <p><span className="text-slate-500">Last Updated:</span> March 2026</p>
          </div>
        </div>

        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <section className="mb-10">
            <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide mb-4">Important — Please Read Carefully</p>
            <p className="text-slate-300 leading-relaxed">
              These Terms of Service ("Terms") govern access to and use of the NewAudia website, applications, and services 
              (collectively, the "Services") and constitute a legally binding agreement between you and NewAudia Systems, LLC. 
              By accessing or using the Services, you agree to be bound by these Terms in their entirety. If you do not agree, 
              you must not access or use the Services.
            </p>
          </section>

          {/* Company Identification */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Company Identification</h3>
            <p className="text-slate-300 leading-relaxed">
              The Services are operated by <strong className="text-white">NewAudia Systems, LLC</strong>, a limited liability 
              company organized under the laws of the State of Colorado, with its principal place of business in Colorado Springs, 
              Colorado (hereinafter "NewAudia," "Company," "we," "us," or "our"). "NewAudia" is a registered trade name of 
              NewAudia Systems, LLC in the State of Colorado.
            </p>
          </section>

          {/* Document Hierarchy */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Document Hierarchy and Incorporated Agreements</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              These Terms of Service serve as the master agreement governing all use of the NewAudia platform. The following 
              additional policy documents are incorporated herein by reference and form part of the complete legal framework. 
              In the event of a conflict between these Terms and any incorporated document, these Terms control unless the 
              incorporated document expressly states otherwise.
            </p>
            <div className="overflow-x-auto">
              <table data-testid="tos-hierarchy-table" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Document</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Applies To</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Author Publishing Agreement v1.0</td>
                    <td className="py-3 px-4 text-slate-400">Authors & Publishers</td>
                    <td className="py-3 px-4 text-slate-400">Governs Manuscript ingestion, licensing, Commission structure, and termination for content submitters.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Author & Publisher Addendum v1.1</td>
                    <td className="py-3 px-4 text-slate-400">Authors & Publishers</td>
                    <td className="py-3 px-4 text-slate-400">Supplements the Author Publishing Agreement with eligibility, Publisher Portal, and AI processing consent terms.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Acceptable Use Policy v1.1</td>
                    <td className="py-3 px-4 text-slate-400">All Users</td>
                    <td className="py-3 px-4 text-slate-400">Defines prohibited conduct, content standards, and enforcement rights across all user roles.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">AI Usage Policy v1.1</td>
                    <td className="py-3 px-4 text-slate-400">All Users</td>
                    <td className="py-3 px-4 text-slate-400">Governs use of Audia AI reading assistant and the AI narration pipeline; third-party AI disclosures.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Privacy Policy v1.1</td>
                    <td className="py-3 px-4 text-slate-400">All Users</td>
                    <td className="py-3 px-4 text-slate-400">Describes collection, use, disclosure, and retention of personal information and Manuscript data.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">Reader Credit & Billing Policy v1.1</td>
                    <td className="py-3 px-4 text-slate-400">Readers</td>
                    <td className="py-3 px-4 text-slate-400">Governs Credit acquisition, consumption, expiration, billing, refunds, and dispute procedures.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">1. Eligibility and Account Registration</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.1 Age Requirement</h4>
            <p className="text-slate-300 leading-relaxed">
              You must be at least 18 years of age, or the age of majority in your jurisdiction, whichever is greater, to 
              access or use the Services. The Services are not intended for use by minors. By accessing the Services, you 
              represent and warrant that you meet this age requirement. This requirement applies to all user roles, including 
              Readers, Authors, Publishers, and Administrators, consistent with Section 1.1 of the NewAudia Author & Publisher 
              Addendum and the NewAudia Privacy Policy.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.2 Account Registration</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              Certain features of the Services require account registration. When creating an account, you agree to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Provide accurate, current, and complete registration information</li>
              <li>Maintain the accuracy of your information and promptly update it as needed</li>
              <li>Maintain the security and confidentiality of your account credentials</li>
              <li>Accept full responsibility for all activity occurring under your account</li>
              <li>Notify NewAudia immediately of any unauthorized use of your account at <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">info@newaudia.com</a></li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.3 Account Suspension and Termination</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia reserves the right to suspend or terminate accounts that violate these Terms, the Acceptable Use Policy, 
              or any other incorporated agreement, with or without prior notice. See Section 9 for full termination terms.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">2. Description of Services</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia provides an AI-powered audiobook platform and two-sided marketplace, including:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Access to AI-narrated Audiobooks and related media through a credit-based subscription system</li>
              <li>Audia, an AI-powered reading assistant embedded within each Audiobook that allows Readers to ask questions about book content in real time</li>
              <li>Tools for Authors and Publishers to submit EPUB Manuscripts for automated ingestion, AI narration, and publication to the NewAudia marketplace</li>
              <li>A Publisher Portal for managing multi-author catalogs</li>
              <li>Reader engagement features, listening analytics, and Commission reporting for Authors and Publishers</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              The Services may evolve over time. NewAudia reserves the right to modify, add, or discontinue features at any 
              time, with or without notice, without liability to any user, consistent with Section 2.4 of the Author Publishing 
              Agreement.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">3. User Roles and Role-Specific Agreements</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Users may interact with the Services in one or more of the following capacities. Certain roles require agreement 
              to additional documents before accessing role-specific features:
            </p>
            <ul className="list-none text-slate-300 space-y-4 ml-4">
              <li><strong className="text-white">Readers:</strong> Access Audiobooks and use Audia via the credit-based system. Governed by these Terms, the Acceptable Use Policy, the AI Usage Policy, the Privacy Policy, and the Reader Credit & Billing Policy.</li>
              <li><strong className="text-white">Authors:</strong> Submit Manuscripts for ingestion, narration, and publication. In addition to all Reader-applicable documents, Authors must execute the Author Publishing Agreement and Author & Publisher Addendum before submitting Manuscripts.</li>
              <li><strong className="text-white">Publishers:</strong> Manage multi-author catalogs through the Publisher Portal. Subject to the same documents as Authors, plus the Publisher Portal provisions in Section 5 of the Author & Publisher Addendum.</li>
              <li><strong className="text-white">Administrators:</strong> Operate platform management tools. Subject to these Terms and all applicable internal policies.</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              Role-specific capabilities and restrictions are governed by the applicable agreements listed above. In the event 
              of a conflict between role-specific terms and these Terms, the more specific role document controls.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">4. User Content and Submissions</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.1 Ownership</h4>
            <p className="text-slate-300 leading-relaxed">
              You retain ownership of content you submit to the Services ("User Content"). NewAudia does not claim ownership 
              of User Content. For Authors and Publishers, full intellectual property and licensing terms are governed 
              exclusively by Article 4 of the Author Publishing Agreement, which controls over this Section in the event of 
              any conflict.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.2 General License Grant</h4>
            <p className="text-slate-300 leading-relaxed">
              For users who are not Authors or Publishers governed by the Author Publishing Agreement, by submitting User 
              Content you grant NewAudia a non-exclusive, worldwide, royalty-free license to host, store, process, transmit, 
              and display such content solely as necessary to operate and provide the Services. This license terminates when 
              the content is removed from the platform.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.3 Content Representations</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              By submitting any User Content, you represent and warrant that:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>You own or hold all necessary rights to submit the content and grant the licenses described herein</li>
              <li>The content does not violate applicable law, third-party rights, or NewAudia's Acceptable Use Policy</li>
              <li>The content does not contain malware, harmful code, or any mechanism intended to damage or gain unauthorized access to the Services</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.4 Content Removal</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia reserves the right to remove any User Content that violates these Terms, the Acceptable Use Policy, or 
              NewAudia's Community Standards, at its sole and unreviewable discretion, consistent with Article 3.4 of the Author 
              Publishing Agreement and Section 5 of the Acceptable Use Policy.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">5. AI-Assisted Features</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Certain Services use AI-assisted technologies, including the Audia AI reading assistant and the AI narration 
              pipeline. Use of these features is governed in full by the NewAudia AI Usage Policy, which is incorporated herein 
              by reference. By using any AI-assisted feature, you agree to the AI Usage Policy in addition to these Terms. 
              You acknowledge that:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>AI outputs are generated algorithmically and may contain inaccuracies, omissions, or contextual limitations</li>
              <li>AI outputs do not constitute legal, medical, financial, or professional advice of any kind</li>
              <li>You are solely responsible for decisions made in reliance on AI-generated content</li>
              <li>Audia responds only from the content of the specific Audiobook being accessed and has no access to information beyond that title</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              NewAudia disclaims all liability for decisions made in reliance on AI-generated outputs. For full AI feature 
              disclosures, third-party provider information, and prohibited AI use, see the AI Usage Policy.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">6. Payments, Credits, and Transactions</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              Access to certain features of the Services requires payment, including the purchase of Reader Credits or 
              subscription plans. All billing, refund, Credit expiration, and dispute terms are governed in full by the 
              NewAudia Reader Credit & Billing Policy, which is incorporated herein by reference.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.1 Payment Processing</h4>
            <p className="text-slate-300 leading-relaxed">
              All payments are processed by Stripe, our third-party payment processor. Payment card information is handled 
              directly by Stripe and is not stored by NewAudia. By completing any purchase, you authorize NewAudia and Stripe 
              to charge your selected payment method for the amount displayed at the time of transaction.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.2 Subscription Billing</h4>
            <p className="text-slate-300 leading-relaxed">
              Subscription plans are billed on a recurring basis. You authorize NewAudia and Stripe to charge your payment 
              method at the start of each billing period. Cancellation instructions and terms are set forth in Section 5.3 
              of the Reader Credit & Billing Policy.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.3 Manuscript Ingestion Fees — Authors and Publishers</h4>
            <p className="text-slate-300 leading-relaxed">
              The first successful Manuscript ingestion per Author account is provided at no charge. Each subsequent successful 
              ingestion is assessed a fee of <strong className="text-white">$9.99 USD</strong>, charged via Stripe upon pipeline 
              completion. All ingestion fees are non-refundable once a Manuscript has been submitted for processing, regardless 
              of outcome. Full ingestion fee terms are set forth in Article 5 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">6.4 Pricing Changes</h4>
            <p className="text-slate-300 leading-relaxed">
              Prices, billing models, and credit structures may change at any time. Current pricing is published on the 
              NewAudia Pricing page at{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>. 
              NewAudia will use commercially reasonable efforts to notify users of material pricing changes in advance.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">7. Acceptable Use</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              All use of the Services is subject to the NewAudia Acceptable Use Policy, which is incorporated herein by 
              reference and sets forth the complete list of prohibited conduct and content standards. The following represents 
              a summary of core restrictions; the Acceptable Use Policy governs in full. You agree not to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Use the Services for any unlawful, fraudulent, or deceptive purpose</li>
              <li>Circumvent access controls, usage limits, billing systems, or security mechanisms</li>
              <li>Interfere with or degrade the performance or availability of the Services</li>
              <li>Upload malware, harmful code, or any content intended to damage the Services or its users</li>
              <li>Misrepresent your identity, affiliation, or the ownership of submitted content</li>
              <li>Use the Services to infringe any copyright, trademark, or other intellectual property right</li>
              <li>Engage in any conduct that NewAudia determines, in its sole discretion, to be abusive, harmful, or contrary to the spirit of these Terms</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              Violation of the Acceptable Use Policy may result in immediate account suspension or permanent ban, forfeiture 
              of credits and unpaid commissions, and pursuit of legal remedies available under applicable law. See Section 5 
              of the Acceptable Use Policy for the full enforcement framework.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">8. Intellectual Property</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.1 NewAudia Platform IP</h4>
            <p className="text-slate-300 leading-relaxed">
              All platform software, infrastructure, branding, trademarks, logos, design elements, and non-user content are 
              owned by or licensed to NewAudia Systems, LLC and are protected by applicable intellectual property laws. 
              "NewAudia" is a registered trade name in the State of Colorado. Except as expressly permitted by these Terms, 
              you may not copy, modify, distribute, sell, sublicense, or exploit any portion of the Services or NewAudia's 
              intellectual property.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.2 Author and Publisher Content</h4>
            <p className="text-slate-300 leading-relaxed">
              Authors and Publishers retain full ownership of their Manuscripts and Audiobooks as set forth in Article 4.1 of 
              the Author Publishing Agreement. NewAudia holds only the non-exclusive license described in Article 4.2 of that 
              Agreement. Nothing in these Terms shall be construed to expand or modify the intellectual property rights and 
              licenses established by the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">8.3 Feedback</h4>
            <p className="text-slate-300 leading-relaxed">
              If you submit feedback, suggestions, or ideas regarding the Services to NewAudia, you grant NewAudia a 
              non-exclusive, perpetual, irrevocable, royalty-free license to use, reproduce, and incorporate such feedback 
              without any obligation of confidentiality or compensation to you.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">9. Termination</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">9.1 Termination by NewAudia</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia may suspend or terminate your access to the Services at any time, with or without notice, if:
            </p>
            <ul className="list-none text-slate-300 space-y-1 ml-4 text-sm">
              <li>(a) you violate these Terms or any incorporated agreement;</li>
              <li>(b) NewAudia is required to do so by applicable law;</li>
              <li>(c) the Services are discontinued; or</li>
              <li>(d) NewAudia determines, in its sole discretion, that your continued access is contrary to the interests of the platform or its users.</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">9.2 Termination by You</h4>
            <p className="text-slate-300 leading-relaxed">
              You may terminate your account at any time by contacting{' '}
              <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">info@newaudia.com</a>. 
              Termination does not entitle you to a refund of any purchased Credits or subscription payments, consistent with 
              the Reader Credit & Billing Policy. For Authors and Publishers, termination procedures are governed by Article 7.2 
              of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">9.3 Permanent Account Ban</h4>
            <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-6 mt-2">
              <h5 className="text-lg font-semibold text-red-400 mb-3">Permanent Ban Policy</h5>
              <p className="text-slate-300 leading-relaxed text-sm mb-3">
                In cases of egregious, repeated, or malicious violations of these Terms or any incorporated agreement, 
                NewAudia may permanently ban a user's account and all associated or related accounts. A permanent ban results in:
              </p>
              <ul className="list-none text-slate-300 space-y-1 text-sm">
                <li>(a) immediate termination of all platform access;</li>
                <li>(b) forfeiture of any remaining Reader Credits, unpaid Commissions, or other accrued platform value;</li>
                <li>(c) non-refundability of all fees and payments made; and</li>
                <li>(d) prohibition on creating new accounts under different names, email addresses, or entities.</li>
              </ul>
              <p className="text-slate-300 leading-relaxed text-sm mt-3">
                NewAudia's determination is made at its sole discretion and is not subject to appeal.
              </p>
            </div>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">9.4 Surviving Provisions</h4>
            <p className="text-slate-300 leading-relaxed">
              The following provisions survive termination of these Terms: Section 4.3 (Content Representations), Section 8 
              (Intellectual Property), Section 10 (Disclaimer of Warranties), Section 11 (Limitation of Liability), Section 12 
              (Indemnification), Section 13 (Governing Law and Dispute Resolution), and this Section 9.4. For Authors and 
              Publishers, the surviving provisions of Article 7.6 of the Author Publishing Agreement also survive.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">10. Disclaimer of Warranties</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
              The Services are provided "as is" and "as available" without warranty of any kind, whether express, implied, or 
              statutory. NewAudia Systems, LLC disclaims all warranties, including but not limited to implied warranties of 
              merchantability, fitness for a particular purpose, title, and non-infringement. NewAudia does not warrant that the 
              Services will be error-free, uninterrupted, secure, or free of harmful components. NewAudia does not warrant that 
              AI-generated outputs will be accurate, complete, or suitable for any particular purpose.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">11. Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide mb-3">
              To the maximum extent permitted by applicable law, NewAudia Systems, LLC and its members, managers, officers, 
              employees, contractors, and affiliates shall not be liable for any indirect, incidental, special, consequential, 
              exemplary, or punitive damages, including lost profits, lost data, lost credits, or loss of goodwill, arising out 
              of or related to these Terms or your use of the Services, even if advised of the possibility of such damages. In 
              no event shall NewAudia's total cumulative liability to you for all claims arising under or related to these Terms 
              exceed the greater of: (a) the total amount paid by you to NewAudia in the three (3) months preceding the claim; 
              or (b) fifty dollars ($50.00 USD). For Authors and Publishers, the limitation of liability set forth in Section 6.3 
              of the Author Publishing Agreement governs claims related to Manuscript ingestion and Audiobook production.
            </p>
            <p className="text-slate-300 leading-relaxed text-sm">
              Some jurisdictions do not allow the exclusion of certain warranties or the limitation of certain damages. To the 
              extent such laws apply, some of the above limitations may not apply to you.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">12. Indemnification</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              You agree to indemnify, defend, and hold harmless NewAudia Systems, LLC and its members, managers, officers, 
              employees, contractors, and affiliates from and against any and all claims, actions, suits, proceedings, 
              liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or 
              related to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Your access to or use of the Services</li>
              <li>Your violation of these Terms or any incorporated agreement</li>
              <li>Any User Content you submit, including any claim that your content infringes, misappropriates, or violates any third-party right</li>
              <li>Your violation of any applicable law or regulation</li>
              <li>Any misrepresentation made by you in connection with your use of the Services</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-4">
              For Authors and Publishers, the indemnification obligations in Article 6.2 of the Author Publishing Agreement 
              also apply and are incorporated herein by reference.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">13. Governing Law and Dispute Resolution</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">13.1 Governing Law</h4>
            <p className="text-slate-300 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Colorado, without 
              regard to its conflict of law principles.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">13.2 Informal Resolution</h4>
            <p className="text-slate-300 leading-relaxed">
              The parties agree to attempt in good faith to resolve any dispute arising under these Terms through informal 
              negotiation for a period of thirty (30) days following written notice of the dispute. If the dispute is not 
              resolved within such period, either party may pursue any legal remedy available under Section 13.3.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">13.3 Jurisdiction and Venue</h4>
            <p className="text-slate-300 leading-relaxed">
              Any legal action arising under or related to these Terms shall be brought exclusively in a court of competent 
              jurisdiction located in El Paso County, Colorado. You hereby irrevocably consent to the personal jurisdiction 
              and venue of such courts and waive any objection to such jurisdiction or venue.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">13.4 Jury Trial Waiver</h4>
            <div className="bg-amber-950/30 border border-amber-900/50 rounded-lg p-5 mt-2">
              <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
                Jury Trial Waiver. To the maximum extent permitted by applicable law, each party hereby irrevocably waives any 
                right to a trial by jury in any action, proceeding, or claim arising under or related to these Terms of Service 
                or the use of the Services.
              </p>
            </div>
          </section>

          {/* Section 14 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">14. General Provisions</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.1 Entire Agreement</h4>
            <p className="text-slate-300 leading-relaxed">
              These Terms, together with the Author Publishing Agreement (where applicable), Author & Publisher Addendum, 
              Acceptable Use Policy, AI Usage Policy, Privacy Policy, and Reader Credit & Billing Policy, constitute the 
              entire agreement between you and NewAudia with respect to your use of the Services, and supersede all prior or 
              contemporaneous agreements, representations, and understandings.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.2 Severability</h4>
            <p className="text-slate-300 leading-relaxed">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall 
              continue in full force and effect. The invalid provision shall be modified to the minimum extent necessary to make 
              it enforceable.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.3 Waiver</h4>
            <p className="text-slate-300 leading-relaxed">
              Failure by NewAudia to enforce any provision of these Terms shall not constitute a waiver of NewAudia's right 
              to enforce such provision in the future.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.4 Assignment</h4>
            <p className="text-slate-300 leading-relaxed">
              You may not assign your rights or delegate your obligations under these Terms without NewAudia's prior written 
              consent. NewAudia may assign these Terms in connection with a merger, acquisition, or sale of all or substantially 
              all of its assets, without your consent.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.5 Force Majeure</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia shall not be liable for any delay or failure in performance resulting from causes beyond its reasonable 
              control, including acts of God, natural disasters, government actions, internet or infrastructure outages, or 
              failures of third-party service providers.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.6 Electronic Agreement</h4>
            <p className="text-slate-300 leading-relaxed">
              By accessing or using the Services, you acknowledge that these Terms constitute a legally binding agreement, and 
              that electronic acceptance (including clicking an acceptance checkbox or continuing to use the Services after 
              notice of updated Terms) has the same legal effect as a handwritten signature.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">14.7 Notices</h4>
            <p className="text-slate-300 leading-relaxed">
              Notices from NewAudia to you will be delivered to the email address on file in your account. Notices from you to 
              NewAudia should be sent to{' '}
              <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">admin@newaudia.com</a>. 
              Email notice is deemed given upon confirmed transmission.
            </p>
          </section>

          {/* Section 15 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">15. Changes to These Terms</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia may update these Terms of Service at any time by posting a revised version on the Services with an 
              updated "Last Updated" date. NewAudia will use reasonable efforts to notify users of material changes via email 
              to the address on file. Continued use of the Services following notice of any update constitutes your acceptance 
              of the revised Terms.
            </p>

            <h4 className="text-lg font-medium text-white mb-3">Version History</h4>
            <div className="overflow-x-auto">
              <table data-testid="tos-version-history" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-20">Version</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-36">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Summary of Changes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">1.0</td>
                    <td className="py-3 px-4 text-slate-400">January 1, 2026</td>
                    <td className="py-3 px-4 text-slate-400">Initial publication. Entity listed as NewAudia dba Use What You Got Publishing, LLC. General platform terms.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">1.1</td>
                    <td className="py-3 px-4 text-slate-300">March 2026</td>
                    <td className="py-3 px-4 text-slate-400">
                      Entity updated to NewAudia Systems, LLC (registered trade name in Colorado). Added Company Identification 
                      and Document Hierarchy sections with incorporated agreements table; expanded eligibility to include age 
                      and registration requirements; defined user roles with role-specific agreement mapping; added User Content 
                      ownership, license, and representations sections; added AI-Assisted Features section with cross-reference 
                      to AI Usage Policy; expanded payments section with ingestion fee terms ($9.99 USD); added Acceptable Use 
                      summary with enforcement consequences; expanded IP section with platform IP, Author content, and feedback 
                      provisions; added full termination framework with permanent ban policy; added disclaimer of warranties, 
                      limitation of liability, and indemnification; added governing law (Colorado), jurisdiction (El Paso County), 
                      informal resolution, and jury trial waiver; added general provisions (entire agreement, severability, 
                      waiver, assignment, force majeure, electronic agreement, notices); added version history table.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
            <div data-testid="tos-contact-info" className="bg-slate-900/50 border border-slate-700 rounded-lg p-6">
              <p className="text-white font-semibold text-lg">NewAudia Systems, LLC</p>
              <p className="text-slate-400 text-sm">A Colorado Limited Liability Company</p>
              <p className="text-slate-400 text-sm">"NewAudia" is a registered trade name in Colorado</p>
              <p className="text-slate-400 mt-2">Colorado Springs, Colorado, USA</p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">General Inquiries:</span>
                  <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">info@newaudia.com</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">Administrative & Legal Notices:</span>
                  <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">admin@newaudia.com</a>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-3">
                Website: <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              </p>
            </div>
          </section>

          {/* Footer */}
          <section className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              NewAudia Systems, LLC | Colorado Springs, Colorado |{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              {' '}| <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">admin@newaudia.com</a>
            </p>
            <p className="text-sm text-slate-500 mt-2 italic">
              "NewAudia" is a registered trade name of NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-sm text-slate-500 mt-1">Version 1.1 — March 2026</p>
          </section>
        </div>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default TermsOfServicePage;
